import { useState, useMemo, useEffect } from "react";
import {
    Box,
    FormControl,
    FormLabel,
    FormHelperText,
    Input,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Checkbox,
    Text,
    Flex,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Badge,
    VStack,
    HStack,
} from "@chakra-ui/react";
import { Button } from "am-tax-fe-core";
import { Control, useController, FieldValues, Path } from "react-hook-form";
import { useGetTransactionsByClientId } from "../../../api/transactions/transactionQueries";
import { Transaction } from "../../../api/transactions/transactionApi";
import { useGetEntitiesByClientId } from "../../../api/entities/entityQueries";
import { useGetReportTransactions } from "../../../api/reportTransactions/reportTransactionQueries";
import {
    getTransactionTypeDisplayName,
    getMethodDisplayName,
    getCategoryDisplayName,
    TransactionType,
    Method,
    Category,
} from "../../../utils/transactionDisplayNames";
import { createGetEntityNameById, getCategoryBadgeColor, getMethodBadgeColor } from "../../../utils/transactionHelpers";

interface Props<T extends FieldValues = { transactionIds: string[] }> {
    control: Control<T>;
    name: Path<T>;
    clientId: string;
    reportId?: string; // Add reportId prop for loading existing transactions when editing
    isRequired?: boolean;
}

export const TransactionSelector = <T extends { transactionIds: string[] }>({ control, name, clientId, reportId, isRequired = false }: Props<T>) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const { field, fieldState } = useController({
        control,
        name,
        rules: { required: isRequired ? "Please select at least one transaction" : false },
    });

    // Get all transactions for the client
    const { data: allTransactions = [], isLoading: isLoadingTransactions } = useGetTransactionsByClientId(clientId);

    // Get entities for display names
    const { data: entities = [] } = useGetEntitiesByClientId(clientId);

    // Helper function to get entity name by ID
    const getEntityNameById = useMemo(() => createGetEntityNameById(entities), [entities]);

    // Get currently selected transactions for the report (if editing)
    const { data: reportTransactions } = useGetReportTransactions(reportId || "");

    // Extract transaction IDs from report transactions when editing
    const existingTransactionIds = useMemo(() => {
        if (!reportTransactions || !reportId) return [];
        return reportTransactions?.transactionIds || [];
    }, [reportTransactions, reportId]);

    // Populate form field with existing transaction IDs when editing
    useEffect(() => {
        if (reportId && existingTransactionIds.length > 0 && (!field.value || field.value.length === 0) && !hasUserInteracted) {
            field.onChange(existingTransactionIds);
        }
    }, [existingTransactionIds, reportId, field, hasUserInteracted]);

    // Filter transactions based on search term
    const filteredTransactions = useMemo(() => {
        if (!searchTerm) return allTransactions;

        const term = searchTerm.toLowerCase();
        return allTransactions.filter(
            (transaction: Transaction) =>
                transaction.name?.toLowerCase().includes(term) ||
                transaction.type?.toLowerCase().includes(term) ||
                transaction.category?.toLowerCase().includes(term) ||
                getEntityNameById(transaction.serviceProviders)?.join(", ")?.toLowerCase().includes(term) ||
                getEntityNameById(transaction.serviceRecipients)?.join(", ")?.toLowerCase().includes(term) ||
                transaction.method?.toLowerCase().includes(term) ||
                getEntityNameById(transaction.testedParties)?.join(", ")?.toLowerCase().includes(term),
        );
    }, [allTransactions, searchTerm, getEntityNameById]);

    // Get selected transaction IDs from form state
    const selectedTransactionIds: string[] = Array.isArray(field.value) ? field.value : field.value ? [field.value] : [];

    // Handle transaction selection
    const handleTransactionSelect = (transactionId: string, isChecked: boolean) => {
        setHasUserInteracted(true);
        let newSelection: string[];

        if (isChecked) {
            newSelection = [...selectedTransactionIds, transactionId];
        } else {
            newSelection = selectedTransactionIds.filter(id => id !== transactionId);
        }

        field.onChange(newSelection);
    };

    // Handle select all
    const handleSelectAll = async () => {
        setHasUserInteracted(true);
        const allTransactionIds = filteredTransactions.map((t: Transaction) => t.id);
        field.onChange(allTransactionIds);
    };

    // Handle clear all
    const handleClearAll = async () => {
        setHasUserInteracted(true);
        field.onChange([]);
    };

    const selectedTransactions = allTransactions.filter((t: Transaction) => selectedTransactionIds.includes(t.id));

    return (
        <FormControl isInvalid={!!fieldState.error}>
            <FormLabel>Transactions</FormLabel>
            {allTransactions.length > 0 ? (
                <>
                    <Box border="1px" borderColor="gray.200" borderRadius="md" p={3}>
                        <Flex justify="space-between" align="center" mb={2}>
                            <Text fontSize="sm">{selectedTransactionIds.length} transaction(s) selected</Text>
                            <Button size="sm" onClick={async () => onOpen()}>
                                Select Transactions
                            </Button>
                        </Flex>

                        {/* Show selected transactions summary */}
                        {selectedTransactions.length > 0 && (
                            <VStack align="start" spacing={1}>
                                {selectedTransactions.slice(0, 3).map((transaction: Transaction) => (
                                    <HStack key={transaction.id} spacing={2}>
                                        <Badge colorScheme={getCategoryBadgeColor(transaction.category)} size="sm">
                                            {getCategoryDisplayName(transaction.category as Category)}
                                        </Badge>
                                        <Text fontSize="xs">{transaction.name}</Text>
                                        <Text fontSize="xs">{getTransactionTypeDisplayName(transaction.type as TransactionType)}</Text>
                                    </HStack>
                                ))}
                                {selectedTransactions.length > 3 && (
                                    <Text fontSize="xs" color="gray.500">
                                        ... and {selectedTransactions.length - 3} more
                                    </Text>
                                )}
                            </VStack>
                        )}
                    </Box>

                    <FormHelperText>Select the transactions to include in this report</FormHelperText>

                    {/* Transaction Selection Modal */}
                    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Flex align="center">
                                    <Text>Select Transactions</Text>
                                    <HStack spacing={2} pl={2}>
                                        <Button size="sm" variant="outline" onClick={handleSelectAll}>
                                            Select All ({filteredTransactions.length})
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={handleClearAll}>
                                            Clear All
                                        </Button>
                                    </HStack>
                                </Flex>
                            </ModalHeader>
                            <ModalCloseButton />

                            <ModalBody>
                                {/* Search input */}
                                <Box mb={4}>
                                    <Input placeholder="Search transactions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                </Box>

                                {/* Transaction table */}
                                <Box maxHeight="400px" overflowY="auto">
                                    <Table size="sm">
                                        <Thead position="sticky" top={0} bg="white" zIndex={1}>
                                            <Tr>
                                                <Th width="40px">
                                                    <Checkbox
                                                        isChecked={
                                                            selectedTransactionIds.length === filteredTransactions.length && filteredTransactions.length > 0
                                                        }
                                                        isIndeterminate={
                                                            selectedTransactionIds.length > 0 && selectedTransactionIds.length < filteredTransactions.length
                                                        }
                                                        onChange={e => (e.target.checked ? handleSelectAll() : handleClearAll())}
                                                    />
                                                </Th>
                                                <Th>Name</Th>
                                                <Th>Category</Th>
                                                <Th>Transaction Type</Th>
                                                <Th>Service Providers</Th>
                                                <Th>Service Recipients</Th>
                                                <Th>Method</Th>
                                                <Th>Tested Parties</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {isLoadingTransactions ? (
                                                <Tr>
                                                    <Td colSpan={8} textAlign="center">
                                                        Loading transactions...
                                                    </Td>
                                                </Tr>
                                            ) : filteredTransactions.length === 0 ? (
                                                <Tr>
                                                    <Td colSpan={8} textAlign="center">
                                                        {searchTerm ? "No transactions match your search" : "No transactions found"}
                                                    </Td>
                                                </Tr>
                                            ) : (
                                                filteredTransactions.map((transaction: Transaction) => (
                                                    <Tr key={transaction.id}>
                                                        <Td>
                                                            <Checkbox
                                                                isChecked={selectedTransactionIds.includes(transaction.id)}
                                                                onChange={e => handleTransactionSelect(transaction.id, e.target.checked)}
                                                            />
                                                        </Td>
                                                        <Td>
                                                            <Text isTruncated maxWidth="200px">
                                                                {transaction.name}
                                                            </Text>
                                                        </Td>
                                                        <Td>
                                                            <Badge
                                                                colorScheme={getCategoryBadgeColor(transaction.category)}
                                                                variant="subtle"
                                                                fontSize="xs"
                                                                px={2}
                                                                py={1}
                                                                borderRadius="md"
                                                            >
                                                                {getCategoryDisplayName(transaction.category as Category)}
                                                            </Badge>
                                                        </Td>
                                                        <Td>{getTransactionTypeDisplayName(transaction.type as TransactionType)}</Td>
                                                        <Td>{getEntityNameById(transaction.serviceProviders).join(", ")}</Td>
                                                        <Td>{getEntityNameById(transaction.serviceRecipients).join(", ")}</Td>
                                                        <Td>
                                                            <Badge
                                                                colorScheme={getMethodBadgeColor(transaction.method)}
                                                                variant="subtle"
                                                                fontSize="xs"
                                                                px={3}
                                                                py={1}
                                                                borderRadius="full"
                                                                fontWeight="bold"
                                                            >
                                                                {getMethodDisplayName(transaction.method as Method)}
                                                            </Badge>
                                                        </Td>
                                                        <Td>{getEntityNameById(transaction.testedParties).join(", ")}</Td>
                                                    </Tr>
                                                ))
                                            )}
                                        </Tbody>
                                    </Table>
                                </Box>
                            </ModalBody>

                            <ModalFooter>
                                <HStack spacing={3}>
                                    <Text fontSize="sm">
                                        {selectedTransactionIds.length} of {allTransactions.length} transactions selected
                                    </Text>
                                    <Button variant="outline" onClick={async () => onClose()}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={async () => onClose()}>
                                        Done
                                    </Button>
                                </HStack>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>
                </>
            ) : (
                <Box>No transactions found for the client. Please add one to the client before generating a report.</Box>
            )}
        </FormControl>
    );
};
